'use client';

import type React from 'react';

import type { Project } from '@/app/models/overhead';
import type { Provider } from '@/app/models/providersResponse';
import type { ResourceDonorsResponse } from '@/app/models/resourceDonors';
import { useSearchProjectsQuery } from '@/app/services/projectApi';
import { useCreateResourceDonorMutation, useResourceDonorSearchByQuery } from '@/app/services/resourceDonors';
import isValidDecimalInput from '@/app/utils/isValidDecimalInput';
import ModalPageTitle from '@/components/common/ModalPageTitle';
import { Box, Button, Stack, TextField, Typography, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import DrawerButtons from '../common/DrawerButtons';
import GenericAutoComplete from '../common/GenericAutoComplete';
import PercentInput from '../common/PercentInput';

interface NewConsiderCollaboratorProps {
  setDrawerOpen: (open: boolean) => void;
  editDestinationProject?: Provider;
  setEditDestinationProject?: (p: Provider | null) => void;
  editEditOriginProject?: Provider;
  setEditOriginProject?: (p: Provider | null) => void;
  editMaxAllocationPercent?: string;
  editEligiblePercent?: string;
}

export default function NewEditResourceDonors(props: NewConsiderCollaboratorProps) {
  const { t } = useTranslation(['resourceDonor', 'common']);
  const theme = useTheme();
  const {
    setDrawerOpen,
    editDestinationProject,
    setEditDestinationProject,
    editEditOriginProject,
    setEditOriginProject,
    editMaxAllocationPercent,
    editEligiblePercent,
  } = props;

  const [eligiblePercent, setEligiblePercent] = useState<string>(editEligiblePercent || '0');
  const [maxAllocationPercent, setMaxAllocationPercent] = useState<string>(editMaxAllocationPercent || '0');
  const [resourceDonorsData, setResourceDonorsData] = useState<ResourceDonorsResponse | null>(null);
  const [searchTermDonors, setSearchTermDonors] = useState<string | null>('');

  const [originProject, setOriginProject] = useState<Project | null>(null);
  const [originProjectSearch, setOriginProjectSearch] = useState('');
  const { data: originProjects, isFetching: isLoadingOrigin } = useSearchProjectsQuery({
    searchTerm: originProjectSearch,
    bringClosed: false,
    page: 0,
    size: 10,
    sortKey: 'name',
    sortDirection: 'ASC',
  });

  const [destinationProject, setDestinationProject] = useState<Project | null>(null);
  const [destinationProjectSearch, setDestinationProjectSearch] = useState('');
  const { data: destinationProjects, isFetching: isLoadingDestination } = useSearchProjectsQuery({
    searchTerm: destinationProjectSearch,
    bringClosed: false,
    page: 0,
    size: 10,
    sortKey: 'name',
    sortDirection: 'ASC',
  });

  const { data: resourceDonors } = useResourceDonorSearchByQuery(searchTermDonors as string, {
    skip: !searchTermDonors,
  });

  const initialValues = {
    destinationProject: editDestinationProject || null,
    eligiblePercent: editEligiblePercent || '0',
    maxAllocationPercent: editMaxAllocationPercent || '0',
  };

  useEffect(() => {
    if (!searchTermDonors) return;
    if (resourceDonors && resourceDonors?.[0].provider && resourceDonors?.[0].toAllocate && searchTermDonors) {
      const editResourceDonors: ResourceDonorsResponse = {
        eligiblePercent: resourceDonors?.[0].eligiblePercent,
        maxAllocation: resourceDonors?.[0].maxAllocation,
        provider: {
          id: resourceDonors?.[0].provider.id,
          code: resourceDonors?.[0].provider.code,
          name: resourceDonors?.[0].provider.name,
          branchName: resourceDonors?.[0].provider.branchName,
        },
        toAllocate: {
          id: resourceDonors?.[0].toAllocate.id,
          code: resourceDonors?.[0].toAllocate.code,
          name: resourceDonors?.[0].toAllocate.name,
          branchName: resourceDonors?.[0].toAllocate.branchName,
        },
        id: resourceDonors?.[0].id,
      };

      setResourceDonorsData(editResourceDonors);
    }
  }, [resourceDonors, searchTermDonors]);

  const [createDanors] = useCreateResourceDonorMutation();

  const handleSubmit = async () => {
    const provider = editEditOriginProject?.code ?? originProject?.code;
    const destination = editDestinationProject?.code ?? destinationProject?.code;

    const sendDonor = {
      eligiblePercent: Number(eligiblePercent),
      maxAllocation: Number(maxAllocationPercent),
      providerCode: provider!,
      toAllocateCode: destination!,
    };

    await createDanors(sendDonor);
    setDrawerOpen(false);
    toast.success(t('resourceDonor.success.saved'));
  };

  const handleCancel = () => {
    setDrawerOpen(false);
  };

  const hasValuesChanged = () => {
    const currentDestination = editDestinationProject || destinationProject;
    const currentEligible = eligiblePercent;
    const currentMaxAllocation = maxAllocationPercent;

    return (
      currentDestination?.code !== initialValues.destinationProject?.code ||
      currentEligible !== initialValues.eligiblePercent ||
      currentMaxAllocation !== initialValues.maxAllocationPercent
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 24px)',
        maxHeight: 'calc(100vh - 24px)',
        flexGrow: 'inherit',
      }}
    >
      <Box sx={{ flexGrow: 1 }}>
        <ModalPageTitle title={t('new.donor')} subtitle={t('msg.info') as string} />

        <Box
          sx={{
            display: 'block',
            flexDirection: 'row',
            mt: 4,
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ flex: 1, minWidth: theme.spacing(31.25), mb: 4 }}>
            <Typography variant="fieldHeader" sx={{ mt: 2 }}>
              {t('donor.project')}
            </Typography>
            {editEditOriginProject ? (
              <Box>
                <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                  <TextField
                    sx={{ flexGrow: 1 }}
                    size="small"
                    disabled
                    value={`${editEditOriginProject.code} - ${editEditOriginProject.name}`}
                  />
                </Stack>
              </Box>
            ) : (
              <GenericAutoComplete
                value={originProject}
                onChange={(value) => {
                  setOriginProject(value);
                }}
                equalityTest={(option, value) => option?.code === value?.code}
                getOptionLabel={(option) =>
                  `${option?.code} - ${option?.name}${option?.branchName ? ` (${option?.branchName})` : ''}`
                }
                onSearch={(value) => {
                  setOriginProjectSearch(value);
                }}
                options={originProjectSearch !== '' && originProjects?.content ? originProjects?.content : []}
                placeholder={t('common:search.project') || ''}
                loading={isLoadingOrigin}
                sx={{ width: '100%' }}
              />
            )}
          </Box>

          <Box sx={{ flex: 1, minWidth: '250px' }}>
            <Typography variant="fieldHeader" sx={{ mt: 2 }}>
              {t('allocation.project')}
            </Typography>
            {editDestinationProject ? (
              <Box>
                <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                  <TextField
                    sx={{ flexGrow: 1 }}
                    size="small"
                    disabled
                    value={`${editDestinationProject.code} - ${editDestinationProject.name})`}
                  />
                  <Button variant="secondary" onClick={() => setEditDestinationProject?.(null)}>
                    {t('common:change')}
                  </Button>
                </Stack>
              </Box>
            ) : (
              <GenericAutoComplete
                value={destinationProject}
                equalityTest={(option, value) => option?.code === value?.code}
                getOptionLabel={(option) =>
                  `${option?.code} - ${option?.name}${option?.branchName ? ` (${option?.branchName})` : ''}`
                }
                onChange={(value) => {
                  if (value) {
                    setDestinationProject(value);
                  }
                }}
                onSearch={(value) => {
                  setDestinationProjectSearch(value);
                }}
                options={
                  destinationProjectSearch !== '' && destinationProjects?.content ? destinationProjects?.content : []
                }
                placeholder={t('common:search.project') || ''}
                loading={isLoadingDestination}
                sx={{ width: '100%' }}
              />
            )}
          </Box>
        </Box>

        <Box display="flex" gap={6} sx={{ mt: 2 }}>
          <Box display="flex" flexDirection="column">
            <Typography variant="fieldHeader" sx={{ mt: 2 }}>
              {t('eligible.percent')}
            </Typography>
            <PercentInput value={eligiblePercent} onChange={setEligiblePercent} />
          </Box>

          <Box display="flex" flexDirection="column">
            <Typography variant="fieldHeader" sx={{ mt: 2 }}>
              {t('maximum.allocation.percent')}
            </Typography>
            <PercentInput value={maxAllocationPercent} onChange={setMaxAllocationPercent} />
          </Box>
        </Box>
      </Box>

      <Box sx={{ mt: 'auto' }}>
        <DrawerButtons
          onSave={handleSubmit}
          onCancel={handleCancel}
          disabled={
            (!destinationProject && !editDestinationProject) ||
            !eligiblePercent ||
            !maxAllocationPercent ||
            !hasValuesChanged()
          }
        />
      </Box>
    </Box>
  );
}
